import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { getApplicationAlerts } from "@/services/data/sync/application-alerts-sync-coordinator";
import type { ApplicationAlert } from "@/types/entities";
import { useAuthStore } from "@/stores/Auth";
import { usePeriodicTask } from "@/composables/usePeriodicTask";

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const DISMISSAL_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds

// Storage keys for dismissal tracking
const INFO_BANNER_STORAGE_KEY = 'infoBanner_dismissed';
const ALERT_BANNER_STORAGE_KEY = 'alertBanner_dismissed';

export const useApplicationAlertsStore = defineStore("applicationAlerts", () => {
  const { authToken } = storeToRefs( useAuthStore() );
  const alerts = ref<ApplicationAlert[]>([]);
  const isLoading = ref(false);
  const lastFetchTime = ref<number | null>(null);
  const fetchPromise = ref<Promise<ApplicationAlert[]> | null>(null);
  
  // Periodic task for fetching alerts every 60 seconds
  const { 
    isRunning: isPeriodicFetchRunning, 
    lastRunTime: lastPeriodicFetchTime, 
    errorCount: periodicFetchErrorCount,
    start: startPeriodicFetch, 
    stop: stopPeriodicFetch, 
    restart: restartPeriodicFetch 
  } = usePeriodicTask(
    fetchAlerts,
    {
      intervalMs: HEARTBEAT_INTERVAL_MS,
      immediate: false, // Don't start immediately, let the store control when to start
      onError: (error) => {
        console.error('Periodic alerts fetch failed:', error);
      }
    }
  );
  
  // Dismissal state
  const isInfoBannerDismissed = ref(false);
  const isAlertBannerDismissed = ref(false);
  const isDismissalStatusLoaded = ref(false);

  const infoAlerts = computed(() => alerts.value.filter(a => a.type === "information"));
  const issueAlerts = computed(() => alerts.value.filter(a => a.type === "issue"));

  const infoAlert = computed(() => infoAlerts.value.find(a => !a.isResolved && a.status !== "completed"));
  const issueAlert = computed(() => issueAlerts.value.find(a => !a.isResolved && a.status !== "completed"));

  // Computed properties that consider both alert existence and dismissal status
  const shouldShowInfoBanner = computed(() => 
    isDismissalStatusLoaded.value && 
    !isInfoBannerDismissed.value && 
    !!infoAlert.value
  );

  const shouldShowAlertBanner = computed(() => 
    isDismissalStatusLoaded.value && 
    !isAlertBannerDismissed.value && 
    !!issueAlert.value
  );

  /**
   * Checks if cached data is still valid (less than 5 minutes old)
   */
  const isCacheValid = computed(() => {
    if (!lastFetchTime.value) return false;
    return (Date.now() - lastFetchTime.value) < CACHE_DURATION_MS;
  });

  /**
   * Check dismissal status from localStorage
   */
  function checkDismissalStatus(storageKey: string): boolean {
    try {
      const dismissedData = localStorage.getItem(storageKey);
      
      if (dismissedData) {
        const { timestamp } = JSON.parse(dismissedData);
        const now = Date.now();
        
        // If less than 24 hours have passed, keep banner dismissed
        if (now - timestamp < DISMISSAL_DURATION_MS) {
          return true;
        } else {
          // More than 24 hours passed, show banner and clean up old storage
          localStorage.removeItem(storageKey);
          return false;
        }
      }
      
      return false; // Not dismissed
    } catch (error) {
      console.error('Error checking dismissal status:', error);
      return false; // On error, show the banner
    }
  }

  /**
   * Initialize dismissal status from localStorage
   */
  function initializeDismissalStatus(): void {
    isInfoBannerDismissed.value = checkDismissalStatus(INFO_BANNER_STORAGE_KEY);
    isAlertBannerDismissed.value = checkDismissalStatus(ALERT_BANNER_STORAGE_KEY);
    isDismissalStatusLoaded.value = true;
  }

  /**
   * Dismiss a banner and store the timestamp
   */
  function dismissBanner(storageKey: string, dismissalRef: any): void {
    const dismissalData = {
      timestamp: Date.now(),
      bannerId: storageKey
    };
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(dismissalData));
      dismissalRef.value = true;
    } catch (error) {
      console.error('Error storing dismissal data:', error);
    }
  }

  /**
   * Dismiss info banner
   */
  function dismissInfoBanner(): void {
    dismissBanner(INFO_BANNER_STORAGE_KEY, isInfoBannerDismissed);
  }

  /**
   * Dismiss alert banner
   */
  function dismissAlertBanner(): void {
    dismissBanner(ALERT_BANNER_STORAGE_KEY, isAlertBannerDismissed);
  }

  /**
   * Force show a dismissed banner (useful for testing or admin override)
   */
  function forceShowInfoBanner(): void {
    localStorage.removeItem(INFO_BANNER_STORAGE_KEY);
    isInfoBannerDismissed.value = false;
  }

  function forceShowAlertBanner(): void {
    localStorage.removeItem(ALERT_BANNER_STORAGE_KEY);
    isAlertBannerDismissed.value = false;
  }

  /**
   * Thread-safe fetch with 5-minute caching
   * Multiple simultaneous calls will share the same promise
   */
  async function fetchAlerts(): Promise<void> {
    // Initialize dismissal status on first fetch
    //if (!isDismissalStatusLoaded.value) {
      initializeDismissalStatus();
    //}

    // Return immediately if cache is still valid
    if (isCacheValid.value && alerts.value.length > 0) {
      return;
    }

    // If already loading, wait for the existing promise
    if (fetchPromise.value) {
      await fetchPromise.value;
      return;
    }

    // Start new fetch
    isLoading.value = true;
    
    fetchPromise.value = (async () => {
      try {
        const fetchedAlerts = await getApplicationAlerts(authToken.value, null);
        
        // Update store state
        alerts.value = fetchedAlerts;
        lastFetchTime.value = Date.now();
        
        return fetchedAlerts;
      } catch (error) {
        console.error("Failed to fetch application alerts:", error);
        alerts.value = [];
        lastFetchTime.value = null;
        throw error;
      } finally {
        isLoading.value = false;
        fetchPromise.value = null;
      }
    })();

    await fetchPromise.value;
  }

  /**
   * Forces a fresh fetch, bypassing cache
   */
  async function refreshAlerts(): Promise<void> {
    lastFetchTime.value = null;
    fetchPromise.value = null;
    await fetchAlerts();
  }

  /**
   * Clears the cache manually
   */
  function clearCache(): void {
    alerts.value = [];
    lastFetchTime.value = null;
    fetchPromise.value = null;
  }

  /**
   * Starts the periodic fetch of alerts every 60 seconds
   */
  function startPeriodicAlertsFetch(): void {
    startPeriodicFetch();
  }

  /**
   * Stops the periodic fetch of alerts
   */
  function stopPeriodicAlertsFetch(): void {
    stopPeriodicFetch();
  }

  /**
   * Restarts the periodic fetch of alerts
   */
  function restartPeriodicAlertsFetch(): void {
    restartPeriodicFetch();
  }

  /**
   * Clear all dismissal data (useful for testing)
   */
  function clearDismissalData(): void {
    localStorage.removeItem(INFO_BANNER_STORAGE_KEY);
    localStorage.removeItem(ALERT_BANNER_STORAGE_KEY);
    isInfoBannerDismissed.value = false;
    isAlertBannerDismissed.value = false;
  }

  return { 
    // Data
    isLoading, 
    infoAlert, 
    issueAlert, 
    infoAlerts, 
    issueAlerts,
    
    // Banner visibility
    shouldShowInfoBanner,
    shouldShowAlertBanner,
    
    // Methods
    fetchAlerts,
    refreshAlerts,
    clearCache,
    
    // Periodic task methods
    startPeriodicAlertsFetch,
    stopPeriodicAlertsFetch,
    restartPeriodicAlertsFetch,
    
    // Dismissal methods
    dismissInfoBanner,
    dismissAlertBanner,
    forceShowInfoBanner,
    forceShowAlertBanner,
    clearDismissalData,
  };
});